// MaeveGreetingHero — the dark-navy first-name greeting banner that opens
// every desktop landing (User Journeys & Service Design v2: "Where do you
// need help today?" / "You don't have to figure this out alone, Sarah").
// Maeve's face floats at the right; the copy sits on the left.
//
// PRESENTATIONAL ONLY. The first-person / first-name greeting copy is
// CGC-gated (DESIGN §1.4 #8, §6.2.1) — callers compose `title`/`subtitle`
// behind the same `firstName` proxy the Maeve-First entry uses (empty JWT
// `name` ⇒ neutral fallback), and pass the already-resolved strings here.
// This component never invents first-person voice.

import type { ReactNode } from 'react';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';

interface MaeveGreetingHeroProps {
  title: string;
  subtitle?: string;
  /** Small uppercase eyebrow above the title (e.g. a section or role label). */
  eyebrow?: string;
  /** Optional CTA / action node rendered under the copy. */
  action?: ReactNode;
  /** Maeve face variant — `warm` on employee/companion surfaces. */
  variant?: 'standard' | 'warm';
  /**
   * Render Maeve's face. Default `true`. Employee-facing surfaces gate this on
   * the CGC sign-off proxy (DESIGN §1.4 #8 / §6.2.1) so the anti-anthropomorphic
   * face doesn't appear before sign-off — the copy is gated the same way.
   */
  showAvatar?: boolean;
}

export function MaeveGreetingHero({
  title,
  subtitle,
  eyebrow,
  action,
  variant = 'standard',
  showAvatar = true,
}: MaeveGreetingHeroProps) {
  return (
    <section className="relative overflow-hidden rounded-2xl bg-ink px-6 py-7 text-white shadow-dark-section sm:px-8 sm:py-8">
      <div className="relative z-10 max-w-2xl">
        {eyebrow ? (
          <p className="mb-2 text-[0.68rem] font-semibold tracking-[0.18em] text-primary uppercase">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-[1.75rem]">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-2 max-w-xl text-sm text-white/70 sm:text-base">{subtitle}</p>
        ) : null}
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
      {/* Decorative — meaning lives in the copy (aria-hidden inside MaeveAvatar).
          Hidden on the narrowest screens so the greeting copy owns the width. */}
      {showAvatar ? (
        <div className="pointer-events-none absolute top-1/2 right-4 hidden -translate-y-1/2 sm:right-8 sm:block">
          <MaeveAvatar size={92} variant={variant} />
        </div>
      ) : null}
    </section>
  );
}
